import axios from 'axios';

// 建立 axios 實例
const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    // 注意：Accept-Encoding 是瀏覽器限制的 header，無法通過 JavaScript 設置
    // 瀏覽器會自動處理 Accept-Encoding，API Gateway 會根據瀏覽器發送的 header 自動壓縮回應
  },
});

// 請求攔截器：自動加入 JWT Token
apiClient.interceptors.request.use(
  (config) => {
    // 嘗試從多個可能的 key 獲取 token
    let token = localStorage.getItem('auth_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('jwt_token');

    if (token) {
      // 確保 token 不是空字串
      token = token.trim();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;

        // 從 localStorage 獲取用戶資訊，設置租戶 headers
        const userStr = localStorage.getItem('auth_user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            if (user.platformId) {
              config.headers['X-Platform-ID'] = user.platformId;
            }
            if (user.clientId) {
              config.headers['X-Client-ID'] = user.clientId;
            }
          } catch (e) {
            // 解析失敗時不帶租戶 headers
          }
        }
      }
    } else {
      // 如果是需要認證的 API（不是公開 API），且沒有 token，導向登入頁
      if (!config.url.includes('/public/') && !config.url.includes('/auth/')) {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/system')) {
          window.location.href = '/system/login';
        } else if (currentPath.startsWith('/platform')) {
          window.location.href = '/platform/login';
        } else if (currentPath.startsWith('/client')) {
          window.location.href = '/client/login';
        } else {
          window.location.href = '/system/login';
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器：統一錯誤處理
apiClient.interceptors.response.use(
  (response) => {
    // 二進位回應（Excel 等）不可套用下方 JSON 包裝邏輯，否則 Blob 會被誤包成 { success, data }
    const responseType = response.config?.responseType;
    if (responseType === 'blob' && response.data instanceof Blob) {
      return response.data;
    }
    if (responseType === 'arraybuffer' && response.data instanceof ArrayBuffer) {
      return response.data;
    }

    // 後端返回的格式是 { success: true, data: {...}, message: '...' }
    // axios 會包裝成 { data: { success: true, data: {...}, message: '...' } }
    // 我們需要返回 response.data，這樣前端收到的就是 { success: true, data: {...}, message: '...' }
    if (response && response.data) {
      // 如果 response.data 是字串，嘗試解析（可能是 Fastify 發送的 JSON 字串）
      let parsedData = response.data;
      if (typeof response.data === 'string') {
        try {
          parsedData = JSON.parse(response.data);
        } catch (e) {
          // 解析失敗
        }
      }

      // 檢查回應格式：如果沒有 success 欄位，可能是直接返回的資料物件
      if (parsedData && typeof parsedData === 'object' && !parsedData.hasOwnProperty('success')) {
        // 包裝成標準格式
        return {
          success: true,
          data: parsedData,
          message: '操作成功'
        };
      }

      return parsedData;
    }
    // 如果沒有 data，返回一個錯誤格式的物件，讓前端能夠檢測到問題
    return { success: false, message: 'API 回應格式錯誤：缺少 data 欄位' };
  },
  (error) => {
    // 處理錯誤回應
    if (error.response) {
      const { status, data } = error.response;

      // 401 未授權
      if (status === 401) {
        // 如果是登入 API 的 401，不要重定向，讓前端顯示錯誤訊息
        const isLoginApi = error.config.url && (
          error.config.url.includes('/auth/system/login') ||
          error.config.url.includes('/auth/platform/login') ||
          error.config.url.includes('/auth/client/login')
        );

        if (!isLoginApi) {
          // 不是登入 API 的 401，清除 token 並導向登入頁
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_account_type');

          // 根據當前路徑決定導向哪個登入頁
          const currentPath = window.location.pathname;
          if (currentPath.startsWith('/system')) {
            window.location.href = '/system/login';
          } else if (currentPath.startsWith('/platform')) {
            window.location.href = '/platform/login';
          } else if (currentPath.startsWith('/client')) {
            window.location.href = '/client/login';
          } else {
            window.location.href = '/system/login';
          }
        }
      }

      // 如果 data 是字符串，嘗試解析
      let parsedData = data;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (e) {
          // 解析失敗
        }
      }

      // 使用解析後的數據
      const errorData = parsedData || data;

      // 提取錯誤訊息（後端可能為物件、或 JSON 字串、或 Fastify 格式）
      let errorMessage = '發生未知錯誤';
      if (errorData && typeof errorData === 'object') {
        errorMessage =
          errorData.message ||
          errorData.error?.message ||
          (Array.isArray(errorData.errors) && errorData.errors[0] && (errorData.errors[0].message || errorData.errors[0])) ||
          errorMessage;
      } else if (errorData && typeof errorData === 'string' && errorData.trim()) {
        errorMessage = errorData;
      }
      if (errorMessage === '發生未知錯誤') {
        if (status === 401) {
          errorMessage = '帳號或密碼錯誤';
        } else if (status === 403) {
          errorMessage = '無權訪問';
        } else if (status === 404) {
          errorMessage = '資源不存在';
        } else if (status === 500) {
          errorMessage = '伺服器錯誤，請稍後再試';
        } else if (error.message && !error.message.includes('Request failed with status code')) {
          errorMessage = error.message;
        }
      }

      if (errorMessage === '發生未知錯誤' && data != null) {
        try {
          const raw =
            typeof data === 'string'
              ? data
              : typeof errorData === 'object'
                ? JSON.stringify(errorData)
                : String(data);
          if (raw && raw !== '{}' && raw !== 'null') {
            errorMessage = raw.length > 400 ? `${raw.slice(0, 400)}…` : raw;
          }
        } catch (_) {
          /* ignore */
        }
      }
      if (errorMessage === '發生未知錯誤' && status) {
        errorMessage = `請求失敗（HTTP ${status}）`;
      }

      const errorCode = (errorData && errorData.error && errorData.error.code) || 'UNKNOWN_ERROR';
      const errorDetails = (errorData && errorData.error && errorData.error.details) || {};

      // 創建 Error 物件，確保 message 是從後端返回的訊息
      const errorObj = new Error(errorMessage);
      errorObj.code = errorCode;
      errorObj.response = error.response;
      errorObj.details = errorDetails;
      errorObj.data = errorData; // 保存解析後的數據
      errorObj.message = errorMessage; // 確保 message 是正確的錯誤訊息

      return Promise.reject(errorObj);
    }

    // 網路錯誤或 CORS 錯誤
    const networkError = new Error('網路連線錯誤，請檢查您的網路連線');
    networkError.code = 'NETWORK_ERROR';
    networkError.originalError = error;
    return Promise.reject(networkError);
  }
);

export default apiClient;
